"use client";

import { useState, useEffect } from "react";
import { Compass, Settings, DollarSignIcon, HelpCircleIcon } from "lucide-react";
import { driver } from "driver.js";
import startAffiliatesPaymentTour from "@/pages/affiliate/onboarding/Tour/AffiliatesPayoutsTour";
import { useNavigate } from "react-router-dom";
import { useOnboardingFloatingMenu } from "@/hooks/useOnboardingFloatingMenu";
import { useGrandTour } from "@/hooks/useGrandTour";
import startProfileTour from "./ProfileSetupTour";
import startBrandAndProgramsTour from "./BrandsAndProgramsTour";
import startGrandTour from "./GrandTour";
import { Package } from "react-feather";
import startCurrentPageTour from "./page-guides";
import { usePortalScope } from "@/contexts/portalScope";
import { usePortalBranding } from "@/contexts/portalBranding";

interface OnboardingFloatingMenuProps {
  isVisible?: boolean;
}

export default function OnboardingFloatingMenu({
  isVisible: propIsVisible,
}: OnboardingFloatingMenuProps) {
  const { isVisible: hookIsVisible } = useOnboardingFloatingMenu();
  const { isMarketplaceHidden } = usePortalScope();
  const { portalName } = usePortalBranding();
  const { shouldShowTour } = useGrandTour();
  const isVisible = propIsVisible ?? hookIsVisible;
  const [isHovered, setIsHovered] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  const navigate = useNavigate();
  const [currentPageLabel, setCurrentPageLabel] = useState<string | null>(null);
  // Auto-start grand tour if shouldShowTour is true
  useEffect(() => {
    if (shouldShowTour) {
      handleTourStart("grand");
      // Don't complete the tour immediately - let it run until user reaches the last step
    }
  }, [shouldShowTour]);

  const menuItems = [
    {
      id: "profile-tour",
      icon: Settings,
      label: "Profile Setup Tour",
      color: "bg-brand-600 hover:bg-brand-700",
      tour: "profile",
    },
    {
      id: "payment-tour",
      icon: DollarSignIcon,
      label: "Affiliates Payment Tour",
      color: "bg-brand-500 hover:bg-brand-600",
      tour: "payment",
    },
    {
      id: "brands-tour",
      icon: Package,
      label: "Brands Tour",
      color: "bg-brand-500 hover:bg-brand-600",
      tour: "brands",
    },
    {
      id: "current-page-tour",
      icon: HelpCircleIcon,
      label: currentPageLabel ?? "About this page",
      color: "bg-[#E879F9] hover:bg-[#D946EF]",
      tour: "current-page",
    },
  ];

  const handleTourStart = (tourType: string) => {
    const driverInstance = driver();

    const onNextClickProfileTour = (element?: Element) => {
      const currentElement = element?.id;
      if (currentElement === "settings") {
        navigate("/affiliate/settings?tab=personal");
      }

      if (currentElement === "personal") {
        navigate("/affiliate/settings?tab=profile");
      }
      if (currentElement === "profile") {
        navigate("/affiliate/settings?tab=payment");
      }
      if (currentElement === "payment") {
        navigate("/affiliate/settings?tab=payment");
      }
    };

    const onPrevClickProfileTour = (element?: Element) => {
      const previousElement = element?.id;
      if (previousElement === "payment") {
        navigate("/affiliate/settings?tab=profile");
      }
      if (previousElement === "profile") {
        navigate("/affiliate/settings?tab=personal");
      }
      if (previousElement === "personal") {
        navigate("/affiliate/settings");
      }
      if (previousElement === "settings") {
        navigate("/affiliate/dashboard");
      }
    };

    const onNextClickAffiliatePaymentTour = (element?: Element) => {
      const currentElement = element?.id;
      if (currentElement === "payouts") {
        navigate("/affiliate/payouts/history");
      }
    };

    const onPrevClickAffiliatePaymentTour = (element?: Element) => {
      const previousElement = element?.id;
      if (previousElement === "payouts") {
        navigate("/affiliate/dashboard");
      }
      if (previousElement === "payouts-history") {
        navigate("/affiliate/payouts");
      }
    };

    const onNextClickBrandAndProgramTour = (element?: Element) => {
      const currentElement = element?.id;
      if (currentElement === "brands") {
        navigate("/affiliate/brands");
      }

      // The search step is filtered out on a scoped portal, so there is
      // nowhere to navigate to.
      if (currentElement === "active-brands" && !isMarketplaceHidden) {
        navigate("/affiliate/brands/search");
      }
    };

    const onPrevClickBrandAndProgramTour = (element?: Element) => {
      const previousElement = element?.id;
      if (previousElement === "active-brands") {
        navigate("/affiliate/brands");
      }
    };

    const onNextClickGrandTour = (element?: Element) => {
      const currentElement = element?.id;

      
      if (currentElement === "tour-guide-button") {
        setIsHovered(true);
        setHoveredItem(3);
      }

      if (currentElement === "settings") {
        navigate("/affiliate/settings");
        setHoveredItem(null);
        setIsHovered(false);
      }

      // Profile Setup Tour navigation
      if (currentElement === "settings") {
        navigate("/affiliate/settings?tab=personal");
      }
      if (currentElement === "personal") {
        navigate("/affiliate/settings?tab=profile");
      }
      if (currentElement === "profile") {
        navigate("/affiliate/settings?tab=payment");
      }
      if (currentElement === "payment") {
        navigate("/affiliate/settings?tab=payment");
      }

      // Brands and Programs Tour navigation
      if (currentElement === "brands") {
        navigate("/affiliate/brands");
      }
      if (currentElement === "active-brands" && !isMarketplaceHidden) {
        navigate("/affiliate/brands/search");
      }

      // Affiliates Payouts Tour navigation
      if (currentElement === "payouts") {
        navigate("/affiliate/payouts/history");
      }
    };

    const onPrevClickGrandTour = (element?: Element) => {
      const previousElement = element?.id;

      if (previousElement === "settings") {
        setIsHovered(true);
        setHoveredItem(3);
      }

      if (previousElement === "tour-guide-button") {
        setIsHovered(false);
        setHoveredItem(null);
      }


      // Profile Setup Tour navigation
      if (previousElement === "payment") {
        navigate("/affiliate/settings?tab=profile");
      }
      if (previousElement === "profile") {
        navigate("/affiliate/settings?tab=personal");
      }
      if (previousElement === "personal") {
        navigate("/affiliate/settings");
      }
      if (previousElement === "settings") {
        navigate("/affiliate/dashboard");
      }

      // Brands and Programs Tour navigation
      if (previousElement === "active-brands") {
        navigate("/affiliate/brands");
      }
      if (previousElement === "search-brands") {
        navigate("/affiliate/brands");
      }

      // Affiliates Payouts Tour navigation
      if (previousElement === "payouts") {
        navigate("/affiliate/dashboard");
      }
      if (previousElement === "payouts-history") {
        navigate("/affiliate/payouts");
      }
    };

    switch (tourType) {
      case "profile":
        startProfileTour(
          driverInstance,
          onNextClickProfileTour,
          onPrevClickProfileTour
        );
        break;
      case "payment":
        startAffiliatesPaymentTour(
          driverInstance,
          onNextClickAffiliatePaymentTour,
          onPrevClickAffiliatePaymentTour
        );
        break;
      case "brands":
        startBrandAndProgramsTour(
          driverInstance,
          onNextClickBrandAndProgramTour,
          onPrevClickBrandAndProgramTour,
          isMarketplaceHidden
        );
        break;
      case "grand":
        startGrandTour(
          driverInstance,
          onNextClickGrandTour,
          onPrevClickGrandTour,
          isMarketplaceHidden,
          portalName
        );
        break;
      case "current-page":
        startCurrentPageTour(
          driverInstance,
          setCurrentPageLabel
        );
        break;
      default:
        break;
    }
  };

  if (!isVisible) return null;

  return (
    <div
      className="fixed bottom-6 right-6 z-50"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Invisible hover area to keep menu open */}
      <div
        className={`absolute transition-all duration-300 ${
          isHovered
            ? "opacity-0 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        style={{
          width: "200px",
          height: "200px",
          top: "-160px",
          left: "-160px",
          borderRadius: "50%",
        }}
      />

      {/* Menu Items */}
      {menuItems.map((item, index) => {
        const Icon = item.icon;
        const angle = 180 + index * 30; // Distribute from left (180°) to top (270°)
        const radius = 140; // Increased radius for more space between icons and tooltips
        const x = Math.cos((angle * Math.PI) / 180) * radius;
        const y = Math.sin((angle * Math.PI) / 180) * radius;

        return (
          <div
            key={index}
            className={`absolute transition-all duration-300 ease-out ${
              isHovered
                ? "opacity-100 scale-100 pointer-events-auto"
                : "opacity-0 scale-75 pointer-events-none"
            }`}
            style={{
              transform: `translate(${x}px, ${y}px)`,
              transitionDelay: `${index * 50}ms`,
            }}
            onMouseEnter={() => setHoveredItem(index)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            <div id={item.id} className="relative group">
              <button
                onClick={() => handleTourStart(item.tour)}
                className={`${item.color} text-white p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110 backdrop-blur-sm bg-opacity-90`}
              >
                <Icon className="w-5 h-5" />
              </button>

              {/* Label */}
              <div id={item.id}
                className={`absolute right-full top-1/2 -translate-y-1/2 mr-4 transition-all duration-200 ${
                  hoveredItem === index
                    ? "opacity-100 translate-x-0"
                    : "opacity-0 translate-x-2"
                }`}
              >
                <div className="bg-brand-600 text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap shadow-lg backdrop-blur-sm bg-opacity-90">
                  {item.label}
                  <div className="absolute left-full top-1/2 -translate-y-1/2 border-4 border-transparent border-l-brand-600"></div>
                </div>
              </div>
            </div>
          </div>
        );
      })}

      {/* Main Button */}
      <div className="relative">
        <button id="tour-guide-button"
          className={`bg-brand-600 hover:bg-brand-700 text-white p-3 rounded-full shadow-lg transition-all duration-300 backdrop-blur-sm bg-opacity-90 ${
            isHovered ? "rotate-45 scale-110" : "rotate-0 scale-100"
          }`}
        >
          <Compass className="w-5 h-5" />
        </button>

        {/* Main button tooltip */}
        <div
          className={`absolute bottom-full left-1/2 -translate-x-1/2 mt-3 transition-all duration-200 ${
            isHovered
              ? "opacity-100 -translate-y-2"
              : "opacity-0 -translate-y-2"
          }`}
        >
          <div className="bg-brand-600 text-white px-3 py-1 rounded-lg text-sm whitespace-nowrap shadow-lg backdrop-blur-sm bg-opacity-90">
            Take a tour
          </div>
        </div>
      </div>

      {/* Backdrop blur effect */}
      <div
        className={`absolute inset-0 rounded-full transition-all duration-300 ${
          isHovered
            ? "bg-black bg-opacity-5 scale-150"
            : "bg-transparent scale-100"
        }`}
        style={{ zIndex: -1 }}
      />
    </div>
  );
}
